import { useQuery } from '@apollo/client'
import { groupBy } from 'lodash'
import { useParams } from 'react-router-dom'
import { AlbumTreeItem, GetAllPublicAlbumsDocument, GetForeignUserDocument, SortingField } from '../../../../../graphql/generated'

export interface AlbumTree extends AlbumTreeItem {
  children: AlbumTree[]
}

function getAlbumTree(albums: AlbumTreeItem[]) {
  let root: AlbumTree | undefined
  const indexMap: { [key: string]: number } = {}
  const orphans: AlbumTree[] = []
  const nodes: AlbumTree[] = albums.map((album, index) => {
    indexMap[album.id] = index
    return { ...album, children: [] }
  })

  nodes.forEach((node) => {
    // Set the root of tree - My Bucket
    if (node.id === node.parentAlbumId || node.parentAlbumId === null) {
      root = node
      return
    }

    // Retrieve parent node
    if (node.parentAlbumId) {
      const indexOfParent = indexMap[node.parentAlbumId]
      const parent = nodes[indexOfParent]

      if (parent) {
        // Add currrent node to parent's children
        parent.children = [...parent.children, node]
      } else {
        orphans.push(node)
      }
    }
  })

  if (root) {
    // Add orphaned albums to root
    root.children = [...root.children, ...orphans]
  }

  return root
}

export default function useGetPublicAlbums() {
  const params = useParams<{ userId?: string; albumId?: string }>()
  const foreignUser = useQuery(GetForeignUserDocument, {
    variables: { username: params.userId }
  })

  const { data, loading } = useQuery(GetAllPublicAlbumsDocument, {
    variables: { owner: params.userId || '', sortBy: { field: SortingField.DateTaken } }
  })

  const defaultAlbum = foreignUser.data?.getForeignUser.defaultAlbum

  // Set default to empty array to prevent reference error on initial render
  const allAlbums = data?.getAllPublicAlbums ? (data?.getAllPublicAlbums as AlbumTreeItem[]) : []

  const groupedAlbums = groupBy(allAlbums, (album) => (album.parentAlbumId ? album.parentAlbumId : 'parent'))

  // Determine the current album being displayed - If no album id is in the path then must be root album
  const currentAlbum = allAlbums.find((album) => (!params.albumId && album?.id === defaultAlbum) || params.albumId === album?.id)

  const albumTree = getAlbumTree(allAlbums)

  return { currentAlbum, loading, albumTree, groupedAlbums, allAlbums }
}
